import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import "./SupportTickets.css";

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  
  const audioRef = useRef(null);
  const socketRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "https://api.vastrudayam.com/api";
  const SOCKET_URL = API_URL.replace("/api", "");

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/admin/support-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setTickets(res.data.tickets);
      }
    } catch (err) {
      console.error("Fetch tickets error:", err);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();

    // Socket.io for real-time alerts
    socketRef.current = io(SOCKET_URL);

    socketRef.current.on("newSupportTicket", (newTicket) => {
      setTickets((prev) => [newTicket, ...prev]);
      toast.info(`🚨 New Support Request from ${newTicket.name}!`);
      
      // Play Warning Sound
      if (audioRef.current) {
        audioRef.current.play().catch(e => console.log("Audio play blocked by browser", e));
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/admin/support-tickets/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${status}`);
      setTickets(tickets.map((t) => (t.id === id ? { ...t, status } : t)));
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/admin/support-tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ticket deleted");
      setTickets(tickets.filter((t) => t.id !== id));
    } catch (err) {
      toast.error("Failed to delete ticket");
    }
  };

  const filteredTickets = tickets.filter((t) => 
    filter === "All" ? true : t.status === filter
  );

  if (loading) return <div className="loading">Loading tickets...</div>;

  return (
    <div className="support-tickets-container">
      {/* Hidden audio for alerts */}
      <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" preload="auto" />

      <div className="tickets-header">
        <h1>🤖 Support Requests (VastraBot)</h1>
        <div className="header-actions">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="All">All Tickets</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Solved">Solved</option>
          </select>
          <button className="refresh-btn" onClick={fetchTickets}>🔄 Refresh</button>
        </div>
      </div>

      <div className="tickets-grid">
        {filteredTickets.length === 0 ? (
          <div className="no-tickets">No support tickets found.</div>
        ) : (
          filteredTickets.map((ticket) => (
            <div key={ticket.id} className={`ticket-card ${ticket.status.toLowerCase().replace(" ", "-")}`}>
              <div className="ticket-badge">{ticket.status}</div>
              <div className="ticket-main">
                <div className="user-info">
                  <h3>{ticket.name}</h3>
                  <p className="phone">📞 {ticket.phone || "N/A"}</p>
                </div>
                <div className="ticket-msg">
                  <p>"{ticket.message}"</p>
                </div>
                <div className="ticket-meta">
                  <span className="page-url">📍 Page: <a href={ticket.page_url} target="_blank" rel="noreferrer">View Page</a></span>
                  <span className="date">⏰ {new Date(ticket.created_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="ticket-actions">
                <div className="status-btns">
                  <button onClick={() => updateStatus(ticket.id, "Pending")} className="btn-p">Pending</button>
                  <button onClick={() => updateStatus(ticket.id, "In Progress")} className="btn-i">In Progress</button>
                  <button onClick={() => updateStatus(ticket.id, "Solved")} className="btn-s">Solved</button>
                </div>
                <div className="contact-btns">
                  {ticket.phone && (
                    <a 
                      href={`https://wa.me/${ticket.phone.replace(/[^0-9]/g, '')}`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="whatsapp-btn"
                    >
                      WhatsApp User
                    </a>
                  )}
                  <button onClick={() => deleteTicket(ticket.id)} className="delete-btn">🗑️</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SupportTickets;
